import React from "react";
import { useLanguage } from "../../context/LanguageContext";
import { translations } from "../../i18n/translations";
import { motion } from "framer-motion";
import { FormValidationSummary, useFormValidation } from "../common/FormValidationSummary";
import type { FormStepProps } from "../../types/application";
import { isMobile } from "react-device-detect";

// Component for showing extracted data indicator
const ExtractedFieldIndicator: React.FC<{ isExtracted: boolean; t: any }> = ({
  isExtracted,
  t,
}) => {
  if (!isExtracted) return null;

  return (
    <div className="extracted-indicator">
      <svg fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
      {t.extractedFromResume}
    </div>
  );
};

const RequiredQuestionText: React.FC<{ children: React.ReactNode; t: any }> = ({
  children,
  t,
}) => (
  <>
    <strong
      className="required text-red-700 inline-block"
      style={{ width: 0, transform: "translateX(-1rem)" }}
    >
      *
    </strong>
    <span
      className="field-name text-lg"
      style={{ fontSize: "1.125rem", lineHeight: "1.75rem" }}
    >
      {children}
    </span>
    <strong
      className="required text-red-700 text-lg"
      style={{ fontSize: "1.125rem", lineHeight: "1.75rem" }}
    >
      {" "}
      {t.required}
    </strong>
  </>
);

const SecurityEducationStep: React.FC<FormStepProps> = ({
  data,
  onUpdate,
  onNext,
  onPrevious,
}) => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  const validation = useFormValidation({ t });

  const handleEnhancedDataChange = (
    field: string,
    value: string | string[]
  ) => {
    const enhancedData = { ...data.enhancedData, [field]: value };
    onUpdate({ enhancedData });
  };

  const handleSubmit = (e: React.FormEvent) => {
    validation.handleSubmit(e, onNext);
  };

  const enhancedData = data.enhancedData || {};
  const extractedFields = data.extractionMetadata?.extractedFields || [];
  const isFieldExtracted = (fieldName: string) =>
    extractedFields.includes(fieldName);

  const renderFieldHelp = (fieldId: string, helpText: string) => (
    <p id={`${fieldId}-help`} className="text-sm text-gray-700 mt-1 mb-2">
      {helpText}
    </p>
  );

  // Show post-secondary fields if highest education is post-secondary
  const hasPostSecondary =
    enhancedData.highestEducationLevel === "Post-Secondary Education";

  if (isMobile)
    return (
      <motion.form
        ref={validation.formRef}
        onSubmit={handleSubmit}
        onChangeCapture={validation.handleChangeCapture}
        className="wb-frmvld space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        noValidate
      >
        <FormValidationSummary
          t={t}
          errors={validation.errors}
          errorSummaryRef={validation.errorSummaryRef}
          formRef={validation.formRef}
        />
        {/* Header */}
        <div className="text-left">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t.securityEducationSectionTitle}
          </h2>
        </div>

        {/* Section: Security & Education */}
        <div className="contact-form-list">
          <div>
            <label
              htmlFor="validSecurityClearance"
              className="block text-lg font-semibold text-gray-900 mb-2 required"
            >
              20. {t.validSecurityClearanceLabel}{" "}
              <strong className="required">{t.required}</strong>
            </label>
            <select
              id="validSecurityClearance"
              name="validSecurityClearance"
              value={enhancedData.validSecurityClearance || ""}
              onChange={(e) =>
                handleEnhancedDataChange(
                  "validSecurityClearance",
                  e.target.value
                )
              }
              className="form-control"
              required
            >
              <option value="">{t.validSecurityClearancePlaceholder}</option>
              <option value="Reliability Status">
                {t.validSecurityClearanceReliability}
              </option>
              <option value="Enhanced Reliability">
                {t.validSecurityClearanceEnhanced}
              </option>
              <option value="Secret Clearance">
                {t.validSecurityClearanceSecret}
              </option>
              <option value="Other">{t.validSecurityClearanceOther}</option>
              <option value="No">{t.validSecurityClearanceNo}</option>
            </select>
            <ExtractedFieldIndicator
              isExtracted={isFieldExtracted("validSecurityClearance")}
              t={t}
            />
          </div>

          <div>
            <label
              htmlFor="highestEducationLevel"
              className="block text-lg font-semibold text-gray-900 mb-2 required"
            >
              21. {t.highestEducationLevelLabel}{" "}
              <strong className="required">{t.required}</strong>
            </label>
            <p className="text-lg text-gray-900 mb-3">
              {t.highestEducationSubLabel}
            </p>
            <select
              id="highestEducationLevel"
              name="highestEducationLevel"
              value={enhancedData.highestEducationLevel || ""}
              onChange={(e) =>
                handleEnhancedDataChange(
                  "highestEducationLevel",
                  e.target.value
                )
              }
              className="form-control"
              required
            >
              <option value="">{t.highestEducationLevelPlaceholder}</option>
              <option value="Less than High School graduation">
                {t.highestEducationLevelLessThanHS}
              </option>
              <option value="Secondary School Diploma">
                {t.highestEducationLevelSecondary}
              </option>
              <option value="Post-Secondary Education">
                {t.highestEducationLevelPostSecondary}
              </option>
            </select>
            <ExtractedFieldIndicator
              isExtracted={isFieldExtracted("highestEducationLevel")}
              t={t}
            />
          </div>

          {hasPostSecondary && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.3 }}
              className="space-y-6 border-t border-gray-200 pt-6"
              aria-live="polite"
              aria-atomic="true"
            >
              <div>
                <label
                  htmlFor="postSecondaryEducationLevel"
                  className="block text-lg font-semibold text-gray-900 mb-2 required"
                >
                  22. {t.postSecondaryEducationLevelLabel}{" "}
                  <strong className="required">{t.required}</strong>
                </label>
                <select
                  id="postSecondaryEducationLevel"
                  name="postSecondaryEducationLevel"
                  value={enhancedData.postSecondaryEducationLevel || ""}
                  onChange={(e) =>
                    handleEnhancedDataChange(
                      "postSecondaryEducationLevel",
                      e.target.value
                    )
                  }
                  className="form-control"
                  required
                >
                  <option value="">
                    {t.postSecondaryEducationLevelPlaceholder}
                  </option>
                  <option value="Bachelor's Degree">
                    {t.postSecondaryEducationLevelBachelors}
                  </option>
                  <option value="College / Cégep Attestation">
                    {t.postSecondaryEducationLevelAttestation}
                  </option>
                  <option value="College / Cégep Certificate / Diploma">
                    {t.postSecondaryEducationLevelCertificate}
                  </option>
                  <option value="College / Cégep Applied Bachelor">
                    {t.postSecondaryEducationLevelAppliedBachelor}
                  </option>
                  <option value="University Credits / Certificate / Diploma">
                    {t.postSecondaryEducationLevelUniversityCredits}
                  </option>
                  <option value="Master's Degree">
                    {t.postSecondaryEducationLevelMasters}
                  </option>
                  <option value="Doctor of Medicine">
                    {t.postSecondaryEducationLevelMD}
                  </option>
                  <option value="Doctor of Philosophy (PhD)">
                    {t.postSecondaryEducationLevelPhD}
                  </option>
                  <option value="Graduate Diploma (DESS)">
                    {t.postSecondaryEducationLevelDESS}
                  </option>
                </select>
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("postSecondaryEducationLevel")}
                  t={t}
                />
              </div>

              <div>
                <label
                  htmlFor="postSecondaryInstitution"
                  className="block text-lg font-semibold text-gray-900 mb-2 required"
                >
                  23. {t.postSecondaryInstitutionLabel}{" "}
                  <strong className="required">{t.required}</strong>
                </label>
                {renderFieldHelp("postSecondaryInstitution", t.postSecondaryInstitutionPlaceholder)}
                <input
                  type="text"
                  id="postSecondaryInstitution"
                  name="postSecondaryInstitution"
                  value={enhancedData.postSecondaryInstitution || ""}
                  onChange={(e) =>
                    handleEnhancedDataChange(
                      "postSecondaryInstitution",
                      e.target.value
                    )
                  }
                  className="form-control"
                  style={{ minWidth: "300px", maxWidth: "100%" }}
                  aria-describedby="postSecondaryInstitution-help"
                  required
                />
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("postSecondaryInstitution")}
                  t={t}
                />
              </div>

              <div>
                <label
                  htmlFor="areaOfStudy"
                  className="block text-lg font-semibold text-gray-900 mb-2 required"
                >
                  24. {t.areaOfStudyLabel}{" "}
                  <strong className="required">{t.required}</strong>
                </label>
                {renderFieldHelp("areaOfStudy", t.areaOfStudyPlaceholder)}
                <input
                  type="text"
                  id="areaOfStudy"
                  name="areaOfStudy"
                  value={enhancedData.areaOfStudy || ""}
                  onChange={(e) =>
                    handleEnhancedDataChange("areaOfStudy", e.target.value)
                  }
                  className="form-control"
                  style={{ minWidth: "300px", maxWidth: "100%" }}
                  aria-describedby="areaOfStudy-help"
                  required
                />
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("areaOfStudy")}
                  t={t}
                />
              </div>

              <div>
                <label
                  htmlFor="graduationDate"
                  className="block text-lg font-semibold text-gray-900 mb-2 required"
                >
                  25. {t.graduationDateLabel}{" "}
                  <strong className="required">{t.required}</strong>
                </label>
                <input
                  type="date"
                  id="graduationDate"
                  name="graduationDate"
                  max="9999-12-31"
                  value={enhancedData.graduationDate || ""}
                  onChange={(e) =>
                    handleEnhancedDataChange("graduationDate", e.target.value)
                  }
                  className="form-control"
                  required
                />
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("graduationDate")}
                  t={t}
                />
              </div>
            </motion.div>
          )}

          <div>
            <fieldset className="gc-chckbxrdio">
              <legend className="question-legend">
                <RequiredQuestionText t={t}>
                  26. {t.lookingForITRoleLabel}
                </RequiredQuestionText>
              </legend>
              <ul className="list-unstyled lst-spcd-2">
                <li className="radio">
                  <input
                    type="radio"
                    name="lookingForITRole"
                    id="lookingForITRole-yes"
                    value="Yes"
                    checked={enhancedData.lookingForITRole === "Yes"}
                    onChange={(e) =>
                      handleEnhancedDataChange(
                        "lookingForITRole",
                        e.target.value
                      )
                    }
                    required
                  />
                  <label htmlFor="lookingForITRole-yes">
                    {t.lookingForITRoleYes}
                  </label>
                </li>
                <li className="radio">
                  <input
                    type="radio"
                    name="lookingForITRole"
                    id="lookingForITRole-other"
                    value="other"
                    checked={enhancedData.lookingForITRole === "other"}
                    onChange={(e) =>
                      handleEnhancedDataChange(
                        "lookingForITRole",
                        e.target.value
                      )
                    }
                    required
                  />
                  <label htmlFor="lookingForITRole-other">
                    {t.lookingForITRoleOther}
                  </label>
                </li>
              </ul>
            </fieldset>
            <ExtractedFieldIndicator
              isExtracted={isFieldExtracted("lookingForITRole")}
              t={t}
            />
          </div>
        </div>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onPrevious}
            className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t.previousButton}
          </button>
          <button
            type="submit"
            className="w-full sm:w-auto px-6 py-3 rounded-lg font-medium transition-colors primary-action-button"
          >
            {t.nextITCompetenciesButton}
          </button>
        </div>
      </motion.form>
    );
  else
    return (
      <motion.form
        ref={validation.formRef}
        onSubmit={handleSubmit}
        onChangeCapture={validation.handleChangeCapture}
        className="wb-frmvld space-y-20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        noValidate
      >
        <FormValidationSummary
          t={t}
          errors={validation.errors}
          errorSummaryRef={validation.errorSummaryRef}
          formRef={validation.formRef}
        />
        {/* Section: Security & Education */}
        <div className="space-y-8">
          {/* Security Clearance */}
          <div className="space-y-2">
            <label
              htmlFor="validSecurityClearance-desktop"
              className="block text-lg font-semibold text-gray-900 required"
            >
              20. {t.validSecurityClearanceLabel}{" "}
              <strong className="required">{t.required}</strong>
            </label>
            <select
              id="validSecurityClearance-desktop"
              name="validSecurityClearance"
              value={enhancedData.validSecurityClearance || ""}
              onChange={(e) =>
                handleEnhancedDataChange(
                  "validSecurityClearance",
                  e.target.value
                )
              }
              className="form-control"
              required
            >
              <option value="">{t.validSecurityClearancePlaceholder}</option>
              <option value="Reliability Status">
                {t.validSecurityClearanceReliability}
              </option>
              <option value="Enhanced Reliability">
                {t.validSecurityClearanceEnhanced}
              </option>
              <option value="Secret Clearance">
                {t.validSecurityClearanceSecret}
              </option>
              <option value="Other">{t.validSecurityClearanceOther}</option>
              <option value="No">{t.validSecurityClearanceNo}</option>
            </select>
            <ExtractedFieldIndicator
              isExtracted={isFieldExtracted("validSecurityClearance")}
              t={t}
            />
          </div>

          {/* Highest Education Level */}
          <div className="space-y-2">
            <label
              htmlFor="highestEducationLevel-desktop"
              className="block text-lg font-semibold text-gray-900 required"
            >
              21. {t.highestEducationLevelLabel}{" "}
              <strong className="required">{t.required}</strong>
            </label>
            <p className="text-lg text-gray-900">
              {t.highestEducationSubLabel}
            </p>
            <select
              id="highestEducationLevel-desktop"
              name="highestEducationLevel"
              value={enhancedData.highestEducationLevel || ""}
              onChange={(e) =>
                handleEnhancedDataChange(
                  "highestEducationLevel",
                  e.target.value
                )
              }
              className="form-control"
              required
            >
              <option value="">{t.highestEducationLevelPlaceholder}</option>
              <option value="Less than High School graduation">
                {t.highestEducationLevelLessThanHS}
              </option>
              <option value="Secondary School Diploma">
                {t.highestEducationLevelSecondary}
              </option>
              <option value="Post-Secondary Education">
                {t.highestEducationLevelPostSecondary}
              </option>
            </select>
            <ExtractedFieldIndicator
              isExtracted={isFieldExtracted("highestEducationLevel")}
              t={t}
            />
          </div>

          {/* Post-Secondary Education Level */}
          {hasPostSecondary && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.3 }}
              className="space-y-8 pt-4"
              aria-live="polite"
              aria-atomic="true"
            >
              <div className="space-y-2">
                <label
                  htmlFor="postSecondaryEducationLevel-desktop"
                  className="block text-lg font-semibold text-gray-900 required"
                >
                  22. {t.postSecondaryEducationLevelLabel}{" "}
                  <strong className="required">{t.required}</strong>
                </label>
                <select
                  id="postSecondaryEducationLevel-desktop"
                  name="postSecondaryEducationLevel"
                  value={enhancedData.postSecondaryEducationLevel || ""}
                  onChange={(e) =>
                    handleEnhancedDataChange(
                      "postSecondaryEducationLevel",
                      e.target.value
                    )
                  }
                  className="form-control"
                  required
                >
                  <option value="">
                    {t.postSecondaryEducationLevelPlaceholder}
                  </option>
                  <option value="Bachelor's Degree">
                    {t.postSecondaryEducationLevelBachelors}
                  </option>
                  <option value="College / Cégep Attestation">
                    {t.postSecondaryEducationLevelAttestation}
                  </option>
                  <option value="College / Cégep Certificate / Diploma">
                    {t.postSecondaryEducationLevelCertificate}
                  </option>
                  <option value="College / Cégep Applied Bachelor">
                    {t.postSecondaryEducationLevelAppliedBachelor}
                  </option>
                  <option value="University Credits / Certificate / Diploma">
                    {t.postSecondaryEducationLevelUniversityCredits}
                  </option>
                  <option value="Master's Degree">
                    {t.postSecondaryEducationLevelMasters}
                  </option>
                  <option value="Doctor of Medicine">
                    {t.postSecondaryEducationLevelMD}
                  </option>
                  <option value="Doctor of Philosophy (PhD)">
                    {t.postSecondaryEducationLevelPhD}
                  </option>
                  <option value="Graduate Diploma (DESS)">
                    {t.postSecondaryEducationLevelDESS}
                  </option>
                </select>
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("postSecondaryEducationLevel")}
                  t={t}
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="postSecondaryInstitution-desktop"
                  className="block text-lg font-semibold text-gray-900 required"
                >
                  23. {t.postSecondaryInstitutionLabel}{" "}
                  <strong className="required">{t.required}</strong>
                </label>
                {renderFieldHelp("postSecondaryInstitution-desktop", t.postSecondaryInstitutionPlaceholder)}
                <input
                  type="text"
                  id="postSecondaryInstitution-desktop"
                  name="postSecondaryInstitution"
                  value={enhancedData.postSecondaryInstitution || ""}
                  onChange={(e) =>
                    handleEnhancedDataChange(
                      "postSecondaryInstitution",
                      e.target.value
                    )
                  }
                  className="form-control"
                  style={{ minWidth: "400px", maxWidth: "100%" }}
                  aria-describedby="postSecondaryInstitution-desktop-help"
                  required
                />
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("postSecondaryInstitution")}
                  t={t}
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="areaOfStudy-desktop"
                  className="block text-lg font-semibold text-gray-900 required"
                >
                  24. {t.areaOfStudyLabel}{" "}
                  <strong className="required">{t.required}</strong>
                </label>
                {renderFieldHelp("areaOfStudy-desktop", t.areaOfStudyPlaceholder)}
                <input
                  type="text"
                  id="areaOfStudy-desktop"
                  name="areaOfStudy"
                  value={enhancedData.areaOfStudy || ""}
                  onChange={(e) =>
                    handleEnhancedDataChange("areaOfStudy", e.target.value)
                  }
                  className="form-control"
                  style={{ minWidth: "400px", maxWidth: "100%" }}
                  aria-describedby="areaOfStudy-desktop-help"
                  required
                />
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("areaOfStudy")}
                  t={t}
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="graduationDate-desktop"
                  className="block text-lg font-semibold text-gray-900 required"
                >
                  25. {t.graduationDateLabel}{" "}
                  <strong className="required">{t.required}</strong>
                </label>
                <input
                  type="date"
                  id="graduationDate-desktop"
                  name="graduationDate"
                  max="9999-12-31"
                  value={enhancedData.graduationDate || ""}
                  onChange={(e) =>
                    handleEnhancedDataChange("graduationDate", e.target.value)
                  }
                  className="form-control"
                  required
                />
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("graduationDate")}
                  t={t}
                />
              </div>
            </motion.div>
          )}

          {/* Looking for IT Role */}
          <div className="space-y-2">
            <fieldset className="gc-chckbxrdio">
              <legend className="question-legend">
                <RequiredQuestionText t={t}>
                  26. {t.lookingForITRoleLabel}
                </RequiredQuestionText>
              </legend>
              <ul className="list-unstyled lst-spcd-2">
                <li className="radio">
                  <input
                    type="radio"
                    name="lookingForITRole"
                    id="lookingForITRole-yes-desktop"
                    value="Yes"
                    checked={enhancedData.lookingForITRole === "Yes"}
                    onChange={(e) =>
                      handleEnhancedDataChange(
                        "lookingForITRole",
                        e.target.value
                      )
                    }
                    required
                  />
                  <label htmlFor="lookingForITRole-yes-desktop">
                    {t.lookingForITRoleYes}
                  </label>
                </li>
                <li className="radio">
                  <input
                    type="radio"
                    name="lookingForITRole"
                    id="lookingForITRole-other-desktop"
                    value="other"
                    checked={enhancedData.lookingForITRole === "other"}
                    onChange={(e) =>
                      handleEnhancedDataChange(
                        "lookingForITRole",
                        e.target.value
                      )
                    }
                    required
                  />
                  <label htmlFor="lookingForITRole-other-desktop">
                    {t.lookingForITRoleOther}
                  </label>
                </li>
              </ul>
            </fieldset>
            <ExtractedFieldIndicator
              isExtracted={isFieldExtracted("lookingForITRole")}
              t={t}
            />
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-6">
          <button
            type="button"
            onClick={onPrevious}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t.previousButton}
          </button>
          <button
            type="submit"
            className="px-6 py-3 rounded-lg font-medium transition-colors primary-action-button"
          >
            {t.nextITCompetenciesButton || t.nextButton}
          </button>
        </div>
      </motion.form>
    );
};

export default SecurityEducationStep;
